import { useTranslation } from '@hooks/useTranslation';

import { CheckCircleFilledIcon } from '../icons/CheckCircleFilledIcon';
import {
  StatusBadge,
  STATUS_BADGE_COMPACT,
  type StatusBadgeConfigMap,
} from '@/components/ui/status-badge';
import type { Order } from './types';

interface PaymentCardProps {
  paymentStatus: Order['payment_status'];
  stripeChargeUrl: string | null;
}

type PaidState = 'paid';

function paidBadgeConfig(label: string): StatusBadgeConfigMap<PaidState> {
  return {
    paid: {
      label,
      icon: <CheckCircleFilledIcon className="size-4 shrink-0 text-confirmed-green" aria-hidden="true" />,
      bgClassName: 'bg-yield-positive-bg',
      borderClassName: 'border-yield-positive-border',
      textClassName: 'text-confirmed-green',
    },
  };
}

// Sits inline at the end of the order source line, so it wears the same compact
// chip as the event status badges rather than a size of its own.
export function PaymentCard({ paymentStatus, stripeChargeUrl }: PaymentCardProps) {
  const { t } = useTranslation();

  // Rendering nothing is the point. Around 60% of settled orders are paid by
  // Xero invoice and read "pending" here, so a placeholder would read as a
  // claim that they are unpaid.
  if (paymentStatus !== 'paid' || !stripeChargeUrl) return null;

  return (
    <a href={stripeChargeUrl} target="_blank" rel="noopener noreferrer" className="inline-flex">
      <StatusBadge
        value="paid"
        config={paidBadgeConfig(t('payment_card.paid'))}
        className={STATUS_BADGE_COMPACT}
      />
    </a>
  );
}
